/**
 * Helpers purs pour compter les utilisateurs d'une autorisation de
 * facon coherente avec ce qui est reellement affiche dans l'UI ,
 * tout en exposant le nombre d'utilisateurs caches ( typiquement les
 * openAdomAdmin filtres a l'affichage ) .
 *
 * <p><b>Contexte</b> ( ticket #521 ) : le backend persiste dans
 * {@code auth.users} l'ensemble des utilisateurs ayant souscrit a une
 * autorisation , y compris ceux dont {@code isApplicationUser} vaut
 * {@code false} ( typique : un {@code openAdomAdmin} qui s'est ajoute
 * lui-meme ) . Cote UI , la liste cochable est filtree sur
 * {@code u.isApplicationUser && u.label !== '_public_'} - ces users
 * "hors application" ne sont donc PAS affiches .
 *
 * <p>Compter {@code auth.users.length} brut donne un nombre superieur
 * au nombre de cases visibles . Inversement , compter uniquement les
 * cases visibles cochees masque la presence des admins dans la base ,
 * ce qui empeche l'utilisateur de comprendre ce qui s'y trouve
 * reellement .
 *
 * <p>Solution retenue : decompte structure
 * {@link AuthorizationUserCounts} expose les deux dimensions pour que
 * l'UI puisse rendre " (N) - dont K administrateur(s) " en restant
 * synchronisee avec l'etat local des checkboxes ( reactivite WYSIWYG ) .
 */

interface AuthUser {
  login?: string;
}

interface VisibleUser {
  label?: string;
  login?: string;
}

export interface AuthorizationUserCounts {
  /**
   * Nombre d'utilisateurs visibles dans la liste cochable qui sont
   * actuellement coches ( etat local de l'UI , potentiellement
   * different de {@code auth.users} tant que la sauvegarde n'est pas
   * effectuee ) .
   */
  visibleChecked: number;
  /**
   * Nombre d'utilisateurs presents dans {@code auth.users} cote BDD
   * mais absents de la liste affichee ( typiquement des
   * {@code openAdomAdmin} filtres a l'affichage ) . Inclus dans le
   * total pour refleter l'etat reel persiste cote serveur .
   */
  hiddenAdmins: number;
  /**
   * Somme {@link visibleChecked} + {@link hiddenAdmins} - reflete le
   * nombre total d'acces a l'autorisation , visible + cache .
   */
  total: number;
}

/**
 * Calcule les decomptes de l'autorisation a partir de l'etat
 * {@code auth.users} persiste , de la liste affichee et d'un predicat
 * {@code isChecked} qui inspecte la reactivite des checkboxes ( cote
 * appelant ) . Le predicat est passe en parametre pour preserver la
 * purete de la fonction et permettre les tests sans framework Vue .
 *
 * @param authUsers liste des users persistes ( {@code auth.users} )
 * @param visibleUsers liste affichee dans la collapse ( generalement
 *        {@code resultFilterUsersByName} )
 * @param isChecked predicat ( label : string ) =&gt; boolean qui dit si
 *        la case d'un user visible est actuellement cochee
 */
export function computeAuthorizationUserCounts(
  authUsers: readonly AuthUser[] | null | undefined,
  visibleUsers: readonly VisibleUser[] | null | undefined,
  isChecked: (label: string) => boolean,
): AuthorizationUserCounts {
  if (!visibleUsers?.length && !authUsers?.length) {
    return { visibleChecked: 0, hiddenAdmins: 0, total: 0 };
  }
  const visibleKeys = new Set<string>();
  (visibleUsers ?? []).forEach((u) => {
    if (u.label) visibleKeys.add(u.label);
    if (u.login) visibleKeys.add(u.login);
  });
  let visibleChecked = 0;
  (visibleUsers ?? []).forEach((u) => {
    const key = u.label ?? u.login;
    if (key && isChecked(key)) visibleChecked++;
  });
  let hiddenAdmins = 0;
  (authUsers ?? []).forEach((u) => {
    if (u.login && !visibleKeys.has(u.login)) hiddenAdmins++;
  });
  return {
    visibleChecked,
    hiddenAdmins,
    total: visibleChecked + hiddenAdmins,
  };
}

/**
 * Conserve pour retro-compatibilite si d'autres modules importent le
 * helper unique cree precedemment . Retourne le total ( = visibleChecked
 * + hiddenAdmins ) en supposant que tous les visibles presents dans
 * {@code authUsers.login} sont coches ( utile cote initialisation ,
 * avant que les checkboxes n'aient un etat local divergent ) .
 */
export function countVisibleUsersInAuthorization(
  authUsers: readonly AuthUser[] | null | undefined,
  visibleUsers: readonly VisibleUser[] | null | undefined,
): number {
  if (!authUsers?.length || !visibleUsers?.length) return 0;
  const visibleKeys = new Set<string>();
  visibleUsers.forEach((u) => {
    if (u.label) visibleKeys.add(u.label);
    if (u.login) visibleKeys.add(u.login);
  });
  let count = 0;
  for (const u of authUsers) {
    if (u.login && visibleKeys.has(u.login)) count++;
  }
  return count;
}
